<template>
  <div class="w-full min-h-screen flex flex-col bg-gray-900">
    <MainNav />
    
    <!-- Development Only Access Control -->
    <div v-if="!isDevelopment" class="flex-1 flex items-center justify-center bg-white">
      <div class="text-center">
        <div class="text-6xl mb-4">🔒</div>
        <h1 class="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h1>
        <p class="text-gray-600 mb-4">This page is only available in development mode.</p>
        <router-link 
          to="/" 
          class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Home
        </router-link>
      </div>
    </div>
    
    <main v-else class="flex-1 bg-white">
      <div class="container mx-auto px-4 py-12 max-w-6xl">
        <!-- Header -->
        <div class="text-center mb-8">
          <h1 class="text-3xl font-bold text-gray-900 mb-2">Inventory Management</h1>
          <p class="text-gray-600">Manage product stock levels</p>
        </div>
        
        <!-- Current Inventory -->
        <div class="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 class="text-xl font-semibold text-gray-900 mb-4">Current Inventory</h2>
          
          <div v-if="loading" class="text-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p class="mt-2 text-gray-600">Loading inventory...</p>
          </div>
          
          <div v-else-if="inventory.length === 0" class="text-center py-8">
            <p class="text-gray-600">No inventory items found. Add some products below.</p>
          </div>
          
          <div v-else class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product ID</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ABV</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="item in inventory" :key="item.productId">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900">{{ item.name || item.productId }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-500">{{ item.productId }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-900">{{ item.abv ? `${item.abv}%` : '-' }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-900 font-medium">{{ item.quantity }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span 
                      :class="{
                        'px-2 inline-flex text-xs leading-5 font-semibold rounded-full': true,
                        'bg-green-100 text-green-800': item.quantity > 10,
                        'bg-yellow-100 text-yellow-800': item.quantity <= 10 && item.quantity > 0,
                        'bg-red-100 text-red-800': item.quantity === 0
                      }"
                    >
                      {{ item.quantity > 10 ? 'In Stock' : item.quantity > 0 ? 'Low Stock' : 'Out of Stock' }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {{ item.lastUpdated ? new Date(item.lastUpdated).toLocaleDateString() : 'Never' }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button 
                      @click="editItem(item)"
                      class="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <!-- Add/Edit Inventory Item -->
        <div class="bg-white rounded-lg shadow-lg p-6">
          <h2 class="text-xl font-semibold text-gray-900 mb-4">
            {{ editingItem ? 'Edit Inventory Item' : 'Add New Inventory Item' }}
          </h2>
          
          <form @submit.prevent="saveItem" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Product ID</label>
                <input 
                  v-model="form.productId"
                  type="text"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 9354520003487"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                <input 
                  v-model="form.name"
                  type="text"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Yumi Rum Highproof"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">ABV (%)</label>
                <input 
                  v-model.number="form.abv"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 40.0"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                <input 
                  v-model.number="form.quantity"
                  type="number"
                  min="0"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>
            </div>
            
            <div class="flex space-x-3">
              <button 
                type="submit"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {{ editingItem ? 'Update' : 'Add' }} Item
              </button>
              
              <button 
                v-if="editingItem"
                type="button"
                @click="cancelEdit"
                class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
        
        <!-- Quick Actions -->
        <div class="bg-white rounded-lg shadow-lg p-6 mt-8">
          <h2 class="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button 
              @click="addSampleData"
              class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Add Sample Data
            </button>
            
            <button 
              @click="refreshInventory"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Refresh Inventory
            </button>
            
            <button 
              @click="exportInventory"
              class="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              Export Data
            </button>
          </div>
        </div>
      </div>
    </main>
    
    <Footer />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import MainNav from '../components/MainNav.vue';
import Footer from '../components/Footer.vue';

const inventory = ref([]);
const loading = ref(false);
const editingItem = ref(null);
const form = ref({
  productId: '',
  name: '',
  abv: null,
  quantity: 0
});

// Development environment check
const isDevelopment = ref(false);

const loadInventory = async () => {
  loading.value = true;
  try {
    const response = await fetch('/api/inventory');
    const data = await response.json();
    
    if (data.success) {
      inventory.value = data.inventory;
    } else {
      console.error('Failed to load inventory:', data.error);
    }
  } catch (error) {
    console.error('Error loading inventory:', error);
  } finally {
    loading.value = false;
  }
};

const saveItem = async () => {
  try {
    const response = await fetch('/api/inventory', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(form.value),
    });
    
    const data = await response.json();
    
    if (data.success) {
      await loadInventory();
      resetForm();
      alert('Inventory updated successfully!');
    } else {
      alert('Error updating inventory: ' + data.error);
    }
  } catch (error) {
    console.error('Error saving item:', error);
    alert('Error saving item');
  }
};

const editItem = (item) => {
  editingItem.value = item;
  form.value = {
    productId: item.productId,
    name: item.name || item.productId,
    abv: item.abv || null,
    quantity: item.quantity
  };
};

const cancelEdit = () => {
  editingItem.value = null;
  resetForm();
};

const resetForm = () => {
  form.value = {
    productId: '',
    name: '',
    abv: null,
    quantity: 0
  };
  editingItem.value = null;
};

const addSampleData = async () => {
  const sampleData = [
    { productId: '9354520003487', name: 'Yumi Rum Highproof', abv: 40.0, quantity: 50 },
    { productId: '9354520001117', name: 'Aged Yumi Rum 2 Years', abv: 42.0, quantity: 30 },
    { productId: '9354520004798', name: 'Aged Rum Lost Time', abv: 45.0, quantity: 20 }
  ];
  
  for (const item of sampleData) {
    try {
      await fetch('/api/inventory', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(item),
      });
    } catch (error) {
      console.error('Error adding sample data:', error);
    }
  }
  
  await loadInventory();
  alert('Sample data added successfully!');
};

const refreshInventory = () => {
  loadInventory();
};

const exportInventory = () => {
  const dataStr = JSON.stringify(inventory.value, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'inventory-export.json';
  link.click();
  URL.revokeObjectURL(url);
};

onMounted(() => {
  // Check if we're in development environment
  isDevelopment.value = import.meta.env.DEV || window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1');
  
  // Only load inventory if in development
  if (isDevelopment.value) {
    loadInventory();
  }
});
</script>
